<script>
import baseMixin from '@/mixins/baseMixin'
import PromiseDialogDto from '@/models/dialog/promiseDialogDto'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'

export default {
  name: 'PromiseDialog',
  mixins: [baseMixin],
  props: {
    width: {
      type: String,
      default: '500',
    },
  },
  data() {
    return {
      showDialog: false,
      resolve: null,
      /**
       * @type {PromiseDialogDto}
       */
      options: null,
    }
  },
  computed: {
    hasOptions() {
      return !!this.options
    },
    isPersistent() {
      if (!this.hasOptions) return false
      return this.options?.persistent
    },
    hasIcon() {
      if (!this.hasOptions) return false
      return !!this.options?.icon
    },
    hasColor() {
      if (!this.hasOptions) return false
      return !!this.options?.color
    },
    hasActions() {
      if (!this.hasOptions) return false
      return !isNullOrEmptyArray(this.options?.actions)
    },
  },
  methods: {
    getActionDisabled(action) {
      const disabled = action.attributes?.disabled
      return typeof disabled === 'function' ? disabled() : disabled
    },
    /**
     * @param {PromiseDialogDto} opts
     */
    async show(opts) {
      this.options = opts || new PromiseDialogDto({})

      return new Promise((resolve) => {
        this.resolve = resolve
        this.showDialog = true
      })
    },
    resolveEvent(value) {
      this.showDialog = false
      this.resolve && this.resolve(value)
    },
  },
}
</script>

<template>
  <!-- Mobile: Bottom Sheet -->
  <v-bottom-sheet
    v-if="isMobile"
    v-model="showDialog"
    :persistent="isPersistent"
    data-testid="promise-dialog"
  >
    <v-sheet v-if="hasOptions" class="promise-dialog-sheet">
      <slot name="icon">
        <div v-if="hasIcon" class="pt-2 pb-4 text-center">
          <v-icon :color="hasColor ? options.color : ''" large>
            {{ options.icon }}
          </v-icon>
        </div>
      </slot>

      <slot name="title">
        <div
          class="text-subtitle-1 text-center mb-0 pb-0 text--dark font-weight-medium px-4"
        >
          {{ options.title }}
        </div>
      </slot>

      <slot name="text">
        <div class="mt-2 px-4">
          {{ options.text }}
        </div>
      </slot>

      <v-divider class="mt-4"></v-divider>

      <div v-if="hasActions" class="d-flex">
        <v-btn
          v-for="action in options.actions"
          :key="action.key"
          v-bind="action.attributes"
          tile
          class="flex-grow-1"
          height="52"
          :disabled="getActionDisabled(action)"
          @click="resolveEvent(action.event)"
        >
          <v-icon v-if="action.icon" left>
            {{ action.icon }}
          </v-icon>
          {{ action.text }}
        </v-btn>
      </div>
    </v-sheet>
  </v-bottom-sheet>

  <!-- Desktop: Dialog -->
  <v-dialog
    v-else
    v-model="showDialog"
    :width="width"
    :persistent="isPersistent"
    data-testid="promise-dialog"
  >
    <v-card v-if="hasOptions" class="rounded-xl">
      <slot name="icon">
        <div v-if="hasIcon" class="pt-6 pb-4">
          <v-icon :color="hasColor ? options.color : ''" large>
            {{ options.icon }}
          </v-icon>
        </div>
      </slot>

      <slot name="title">
        <v-card-text
          class="text-subtitle-1 text-center mb-0 pb-0 text--dark font-weight-medium"
        >
          {{ options.title }}
        </v-card-text>
      </slot>

      <slot name="text">
        <v-card-text class="mt-0">
          {{ options.text }}
        </v-card-text>
      </slot>

      <v-divider></v-divider>

      <v-card-actions v-if="hasActions">
        <v-spacer></v-spacer>

        <v-btn
          v-for="action in options.actions"
          :key="action.key"
          v-bind="action.attributes"
          :disabled="getActionDisabled(action)"
          @click="resolveEvent(action.event)"
        >
          <v-icon v-if="action.icon" left>
            {{ action.icon }}
          </v-icon>
          {{ action.text }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.promise-dialog-sheet {
  border-radius: 16px 16px 0 0;
}
</style>
